import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Alert, Button, Modal, Spinner, Badge } from 'react-bootstrap';
import Template from '../../template';
import { useAuth } from '../../context/auth';
import api from '../../services/api';
import * as S from './styled';

function MeusPagamentos() {
  const { user } = useAuth();
  const [pagamentos, setPagamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showReembolsoModal, setShowReembolsoModal] = useState(false);
  const [selectedPagamento, setSelectedPagamento] = useState(null);
  const [loadingReembolso, setLoadingReembolso] = useState(false);
  const pollingRef = useRef(null);

  const normalizePagamentoStatus = (status) => {
    if (!status) return '';
    const upper = String(status).trim().toUpperCase();
    if (upper === 'APROVADA') return 'APROVADO';
    if (upper === 'EM PROCESSAMENTO') return 'PENDENTE';
    return upper;
  };

  const getPagamentoStatus = (pagamento) => {
    const fromStatus = normalizePagamentoStatus(pagamento?.status);
    if (fromStatus) return fromStatus;
    if (pagamento?.aprovada === true || pagamento?.aprovada === 1) return 'APROVADO';
    return 'PENDENTE';
  };

  const isReembolsado = (pagamento) => pagamento?.reembolsada === true || pagamento?.reembolsada === 1;
  const isPagamentoAprovado = (pagamento) => getPagamentoStatus(pagamento) === 'APROVADO';
  const isPagamentoRecusado = (pagamento) => getPagamentoStatus(pagamento) === 'RECUSADO';
  const isPagamentoPendente = (pagamento) => getPagamentoStatus(pagamento) === 'PENDENTE';

  const formatarStatusPagamento = (pagamento) => {
    const status = getPagamentoStatus(pagamento);
    if (status === 'APROVADO') return 'Aprovado';
    if (status === 'RECUSADO') return 'Recusado';
    return 'Pendente';
  };

  // Buscar pagamentos do usuário
  const fetchPagamentos = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/pagamento');
      console.log('Dados dos pagamentos:', response.data.data);
      
      if (response.data && response.data.data) {
        setPagamentos(response.data.data);

        // Iniciar polling se houver pagamentos pendentes
        const pendentes = response.data.data.filter(p => isPagamentoPendente(p) && !isReembolsado(p) && p.asaas_id);
        if (pendentes.length > 0 && !pollingRef.current) {
          pollingRef.current = setInterval(() => {
            setPagamentos(prev => {
              pollPendentes(prev);
              return prev;
            });
          }, 10000);
        }
      } else {
        setPagamentos([]);
      }
    } catch (err) {
      console.error('Erro ao buscar pagamentos:', err);
      setError('Erro ao carregar histórico de pagamentos');
      setPagamentos([]);
    } finally {
      setLoading(false);
    }
  };

  // Solicitar reembolso
  const handleReembolso = async () => {
    if (!selectedPagamento) return;

    try {
      setLoadingReembolso(true);
      setError('');
      setSuccess('');

      await api.patch('/reembolso', {
        asaas_id: selectedPagamento.asaas_id
      });

      // Armazenar flag para mostrar mensagem após reload
      sessionStorage.setItem('reembolsoAprovado', 'true');
      
      // Recarregar a página após o reembolso
      window.location.reload();
    } catch (err) {
      console.error('Erro ao solicitar reembolso:', err);
      
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Erro ao processar solicitação de reembolso');
      }
    } finally {
      setLoadingReembolso(false);
    }
  };

  // Abrir modal de confirmação de reembolso
  const openReembolsoModal = (pagamento) => {
    setSelectedPagamento(pagamento);
    setShowReembolsoModal(true);
  };

  // Fechar modal de reembolso
  const closeReembolsoModal = () => {
    setShowReembolsoModal(false);
    setSelectedPagamento(null);
  };

  // Formatar data
  const formatarData = (dataString) => {
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Verificar se pode solicitar reembolso (dentro de 7 dias)
  const podeReembolsar = (dataCompra) => {
    const agora = new Date();
    const compra = new Date(dataCompra);
    const diffTime = Math.abs(agora - compra);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  // Obter status do pagamento
  const getStatusBadge = (pagamento) => {
    if (isReembolsado(pagamento)) {
      return <Badge bg="warning">Reembolsado</Badge>;
    }
    if (isPagamentoAprovado(pagamento)) {
      return <Badge bg="success">Aprovado</Badge>;
    }
    if (isPagamentoRecusado(pagamento)) {
      return <Badge bg="danger">Recusado</Badge>;
    }
    return <Badge bg="secondary">Pendente</Badge>;
  };

  const pollPendentes = useCallback(async (lista) => {
    const pendentes = lista.filter(p => isPagamentoPendente(p) && !isReembolsado(p) && p.asaas_id);
    if (pendentes.length === 0) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
      return;
    }

    let atualizado = false;
    const novaLista = [...lista];

    for (const pend of pendentes) {
      try {
        const res = await api.get(`/pagamento/${pend.asaas_id}`);
        const statusAtualizado = normalizePagamentoStatus(res.data?.status);
        if (statusAtualizado === 'APROVADO' || statusAtualizado === 'RECUSADO') {
          const idx = novaLista.findIndex(p => p.asaas_id === pend.asaas_id);
          if (idx !== -1) {
            novaLista[idx] = { ...novaLista[idx], status: statusAtualizado };
            atualizado = true;
          }
        }
      } catch (err) {
        // ignora erros de polling silenciosamente
      }
    }

    if (atualizado) {
      setPagamentos(novaLista);
      const aindaPendentes = novaLista.filter(p => isPagamentoPendente(p) && !isReembolsado(p) && p.asaas_id);
      if (aindaPendentes.length === 0) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
  }, []);

  useEffect(() => {
    // Verificar se há flag de reembolso aprovado
    const reembolsoAprovado = sessionStorage.getItem('reembolsoAprovado');
    if (reembolsoAprovado === 'true') {
      setSuccess('Reembolso solicitado com sucesso!');
      sessionStorage.removeItem('reembolsoAprovado');
    }
    
    fetchPagamentos();

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  return (
    <Template>
      <S.Container>
        <S.Header>
          <S.Title>Meus Pagamentos</S.Title>
          <S.Subtitle>Histórico de pagamentos e solicitações de reembolso</S.Subtitle>
        </S.Header>

        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" className="mb-4">
            {success}
          </Alert>
        )}

        {loading ? (
          <S.LoadingContainer>
            <Spinner animation="border" variant="primary" />
            <p>Carregando pagamentos...</p>
          </S.LoadingContainer>
        ) : (
          <S.PagamentosContainer>
            {pagamentos.length === 0 ? (
              <S.EmptyState>
                <h4>Nenhum pagamento encontrado</h4>
                <p>Você ainda não possui histórico de pagamentos.</p>
              </S.EmptyState>
            ) : (
              <S.PagamentosList>
                {pagamentos.map((pagamento) => (
                  <S.PagamentoCard key={pagamento.id}>
                    <S.CardHeader>
                      <S.PlanoInfo>
                        <S.PlanoNome>{pagamento.plano}</S.PlanoNome>
                        <S.PagamentoId>ID: {pagamento.id || pagamento.asaas_id || 'N/A'}</S.PagamentoId>
                      </S.PlanoInfo>
                      {getStatusBadge(pagamento)}
                    </S.CardHeader>

                    <S.CardBody>
                      <S.InfoRow>
                       <S.InfoLabel>Usuário:</S.InfoLabel>
                       <S.InfoValue>{user.nome || 'N/A'}</S.InfoValue>
                     </S.InfoRow>
                    <S.InfoRow>
                      <S.InfoLabel>ID do pagamento:</S.InfoLabel>
                      <S.InfoValue>{pagamento.asaas_id || 'N/A'}</S.InfoValue>
                    </S.InfoRow>
                    <S.InfoRow>
                      <S.InfoLabel>Data da Compra:</S.InfoLabel>
                      <S.InfoValue>{formatarData(pagamento.created_at)}</S.InfoValue>
                    </S.InfoRow>
                    <S.InfoRow>
                      <S.InfoLabel>Status Aprovação:</S.InfoLabel>
                      <S.InfoValue>{formatarStatusPagamento(pagamento)}</S.InfoValue>
                    </S.InfoRow>
                    <S.InfoRow>
                      <S.InfoLabel>Status Reembolso:</S.InfoLabel>
                      <S.InfoValue>{pagamento.reembolsada === true || pagamento.reembolsada === 1 ? 'Solicitado' : 'Não solicitado'}</S.InfoValue>
                    </S.InfoRow>
                    </S.CardBody>

                    {/* Botão de reembolso apenas para pagamentos aprovados e não reembolsados */}
                    {isPagamentoAprovado(pagamento) && !isReembolsado(pagamento) && (
                      <S.CardFooter>
                        {podeReembolsar(pagamento.created_at) ? (
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => openReembolsoModal(pagamento)}
                          >
                            Solicitar Reembolso
                          </Button>
                        ) : (
                          <S.ReembolsoInfo>
                            Prazo para reembolso expirado (7 dias)
                          </S.ReembolsoInfo>
                        )}
                      </S.CardFooter>
                    )}
                  </S.PagamentoCard>
                ))}
              </S.PagamentosList>
            )}
          </S.PagamentosContainer>
        )}

        {/* Modal de Confirmação de Reembolso */}
        <Modal show={showReembolsoModal} onHide={closeReembolsoModal} centered>
          <Modal.Header closeButton={!loadingReembolso}>
            <Modal.Title>Confirmar Reembolso</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedPagamento && (
              <div>
                <p><strong>Plano:</strong> {selectedPagamento.plano}</p>
                <p><strong>ID do Pagamento:</strong> {selectedPagamento.asaas_id}</p>
                <p><strong>Data da Compra:</strong> {formatarData(selectedPagamento.created_at)}</p>
                <hr />
                <p className="text-warning">
                  ⚠️ <strong>Atenção:</strong> Ao confirmar o reembolso, seu plano será alterado para TESTE e você perderá o acesso aos recursos premium.
                </p>
                <p>Deseja realmente solicitar o reembolso?</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={closeReembolsoModal}
              disabled={loadingReembolso}
            >
              Cancelar
            </Button>
            <Button 
              variant="warning" 
              onClick={handleReembolso}
              disabled={loadingReembolso}
            >
              {loadingReembolso ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Processando...
                </>
              ) : (
                'Confirmar Reembolso'
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </S.Container>
    </Template>
  );
}

export default MeusPagamentos;